import React, { useState, useEffect, useRef, useMemo } from 'react';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { MapLayerEnum } from '@/utils/enum';
import TileLayer from 'ol/layer/Tile.js';
import TileWMS from 'ol/source/TileWMS.js';
import { debounceImmediate } from '@/utils/util';
import './index.scss';
import { HttpStatusCode } from '@/fetch/core/constant';
import CollectMapApi from '@/fetch/bussiness/collectMap';
const HighPrecisionTileLayerInfoMap = new Map<MapLayerEnum, any>([
  [
    MapLayerEnum.OPEN_AREA,
    {
      layerId: MapLayerEnum.OPEN_AREA,
      url: `https://${process.env.JDX_APP_MAP_EDITOR}/geoserver/gcj02/wms`,
      params: {
        FORMAT: 'image/png',
        TILED: true,
        LAYERS: `${process.env.JDX_APP_MAP_DATA}:map_free_area`,
        STYLES: 'open-area',
      },
    },
  ],
  [
    MapLayerEnum.LINE_BOUNDARY,
    {
      layerId: MapLayerEnum.LINE_BOUNDARY,
      url: `https://${process.env.JDX_APP_MAP_EDITOR}/geoserver/gcj02/wms`,
      params: {
        FORMAT: 'image/png',
        TILED: true,
        LAYERS: `${process.env.JDX_APP_MAP_DATA}:map_boundary`,
        STYLES: 'line-boundary',
      },
    },
  ],
  [
    MapLayerEnum.GATE_LAYER,
    {
      layerId: MapLayerEnum.GATE_LAYER,
      url: `https://${process.env.JDX_APP_MAP_EDITOR}/geoserver/gcj02/wms`,
      params: {
        FORMAT: 'image/png',
        TILED: true,
        LAYERS: `${process.env.JDX_APP_MAP_DATA}:map_gate`,
        STYLES: 'line-gate',
      },
    },
  ],
  [
    MapLayerEnum.INTERSECTION,
    {
      layerId: MapLayerEnum.INTERSECTION,
      url: `https://${process.env.JDX_APP_MAP_EDITOR}/geoserver/gcj02/wms`,
      params: {
        FORMAT: 'image/png',
        TILED: true,
        LAYERS: `${process.env.JDX_APP_MAP_DATA}:map_intersection`,
        STYLES: 'intersection',
      },
    },
  ],
  [
    MapLayerEnum.CENTER_LINE,
    {
      layerId: MapLayerEnum.CENTER_LINE,
      url: `https://${process.env.JDX_APP_MAP_EDITOR}/geoserver/gcj02/wms`,
      params: {
        FORMAT: 'image/png',
        TILED: true,
        LAYERS: `${process.env.JDX_APP_MAP_DATA}:map_lane`,
        STYLES: 'line-center',
      },
    },
  ],
  [
    MapLayerEnum.STOP_LINE,
    {
      layerId: MapLayerEnum.STOP_LINE,
      url: `https://${process.env.JDX_APP_MAP_EDITOR}/geoserver/gcj02/wms`,
      params: {
        FORMAT: 'image/png',
        TILED: true,
        LAYERS: `${process.env.JDX_APP_MAP_DATA}:map_stop_line`,
        STYLES: 'line-red',
      },
    },
  ],
]);
// 高精地图元素定义
const HDMaps = [
  { key: MapLayerEnum.LINE_BOUNDARY, value: '车道边线' },
  { key: MapLayerEnum.CENTER_LINE, value: '车道中心线' },
  { key: MapLayerEnum.INTERSECTION, value: '路口' },
  { key: MapLayerEnum.OPEN_AREA, value: '开阔区域' },
  { key: MapLayerEnum.GATE_LAYER, value: '大门' },
  { key: MapLayerEnum.STOP_LINE, value: '停止线' },
];

const HighPrecisionBtn = ({
  mapOperateRef,
  forceAllVisible,
  latitude,
  longitude,
}: {
  mapOperateRef: React.RefObject<any>;
  forceAllVisible: boolean;
  latitude: number;
  longitude: number;
}) => {
  const fetchApi = new CollectMapApi();
  const [HDMapsLabelList, setHDMapsLabelList] = useState(() =>
    HDMaps.map(({ key, value }) => ({
      key,
      value,
      visible: false,
    }))
  );
  const [showHDMapsOptions, setShowHDMapsOptions] = useState<boolean>(false);
  const showHighPrecisionMap = HDMapsLabelList.some((item) => item.visible);
  const mapInfoRef = useRef({
    id: null,
    version: null,
  });

  useEffect(() => {
    setHDMapsLabelList((prev) =>
      prev.map((item) => ({
        ...item,
        visible: forceAllVisible,
      }))
    );
  }, [forceAllVisible]);

  useEffect(() => {
    if (latitude && longitude) {
      getMapInfoByPosition();
    }
    if (mapInfoRef.current.id && mapInfoRef.current.version) {
      HDMapsLabelList.forEach((item) => {
        if (!mapOperateRef?.current?.getLayer(item.key) && item.visible) {
          loadLayer(item.key);
        }
        mapOperateRef?.current?.changeLayerVisibility(item.key, item.visible);
      });
    }
  }, [HDMapsLabelList]);

  const getMapInfoByPosition = async () => {
    try {
      const res = await fetchApi.getMapInfoByPosition({
        latitude,
        longitude,
      });
      if (res.code === HttpStatusCode.Success) {
        const newId = res?.data?.mapId;
        const newVersion = res?.data?.mapVersion;
        if (newId && newVersion) {
          if (
            mapInfoRef.current.id !== newId ||
            mapInfoRef.current.version !== newVersion
          ) {
            mapInfoRef.current = {
              id: newId,
              version: newVersion,
            };
          }
          HDMapsLabelList.forEach((item) => {
            if (!mapOperateRef?.current?.getLayer(item.key) && item.visible) {
              loadLayer(item.key);
            }
            mapOperateRef?.current?.changeLayerVisibility(
              item.key,
              item.visible
            );
          });
        }
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // 加载图层
  const loadLayer = (layerKey: MapLayerEnum) => {
    const layerInfo = HighPrecisionTileLayerInfoMap.get(layerKey);
    if (!layerInfo) {
      console.warn(`Layer info not found for ${layerKey}`);
      return;
    }
    const layer = createTileLayer(layerInfo);
    mapOperateRef?.current?.addLayers([
      {
        layer: layer,
        layerId: layerKey,
      },
    ]);
  };
  // 创建WMS图层
  const createTileLayer = (layerInfo: any) => {
    const mapInfo = mapInfoRef.current;
    if (!mapInfo.id || !mapInfo.version) {
      return;
    }
    const cqlFilter = `map_id=${mapInfo.id} and map_version=${mapInfo.version}`;
    return new TileLayer({
      source: new TileWMS({
        url: layerInfo.url,
        params: {
          ...layerInfo.params,
          CQL_FILTER: cqlFilter,
        },
        // 后端服务只提供EPSG:4326投影的坐标系，这里需要显式添加配置
        projection: 'EPSG:4326',
        transition: 300,
        cacheSize: 2048,
      }),
      preload: Infinity,
      useInterimTilesOnError: false,
      visible: false,
    });
  };
  const toggleHighPrecisionMap = () => {
    const newVisibility = !showHighPrecisionMap;
    setHDMapsLabelList((prev) =>
      prev.map((item) => ({
        ...item,
        visible: newVisibility,
      }))
    );
  };

  const changeHDMapsVisible = (item: any) => {
    const newVisibility = !item.visible;
    setHDMapsLabelList((prev) =>
      prev.map((v) =>
        v.key === item.key ? { ...v, visible: newVisibility } : v
      )
    );
  };
  const debouncedSetShowHDMapsOptions = debounceImmediate(
    (value: boolean) => setShowHDMapsOptions(value),
    300
  );

  return (
    <div
      className="high-precision-container"
      onMouseLeave={() => debouncedSetShowHDMapsOptions(false)}
    >
      <div
        className="high-precision-btns"
        onClick={toggleHighPrecisionMap}
        onMouseEnter={() => setShowHDMapsOptions(true)}
      >
        {showHighPrecisionMap ? (
          <EyeOutlined className="eye-icon" />
        ) : (
          <EyeInvisibleOutlined className="eye-icon" color="white" />
        )}
        <span>高精</span>
      </div>

      {showHDMapsOptions && (
        <div className="high-precision-options">
          {HDMapsLabelList.map((item) => (
            <div
              className="high-precision-option"
              key={item.key}
              onClick={() => changeHDMapsVisible(item)}
            >
              {item.visible ? (
                <EyeOutlined className="eye-icon" />
              ) : (
                <EyeInvisibleOutlined className="eye-icon" color="white" />
              )}
              <span>{item.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(HighPrecisionBtn);
